import genererMail from '@/services/general/genererMail';
import { get as getTiers } from '@/api/tiers/index';
import store from '@/store/store';
import { cloneDeep, uniq } from 'lodash';
import { getAllByTiers } from '@/api/contacts';
import i18n from '@/i18n';
import formatDate from '@/services/general/formatDate';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import nomPrenomGetter from '@/services/general/nomPrenomGetter';

export function convertUserToMail(user) {
  return {
    id: user.id,
    email: user.email,
    nomToDisplay: `${user.decorator?.nomPrenom} ${user.decorator?.nomPrenom && user.email ? '-' : ''} ${user.email}`.trim(),
  };
}

export function convertTierToMail(tier) {
  return {
    id: tier.id,
    email: tier.email,
    nomToDisplay: `${tier.nomCommercial} ${tier.nomCommercial && tier.email ? '-' : ''} ${tier.email}`.trim(),
  };
}
export function convertEmailToMail(email) {
  return {
    id: email,
    email: email,
    nomToDisplay: email,
  };
}

function convertContactToMail(contact) {
  const typesContactParam = store.getters['parametres/parametre'](PARAMETRES_KEYS.typeContact);
  let typeContact = '';
  if (contact.typesContactIds) {
    const filter = typesContactParam.filter(el => contact.typesContactIds.includes(el.id));
    if (filter.length > 0) typeContact = filter.map(f => f.libelle).join(', ');
  }
  return {
    id: contact.id,
    email: contact.email,
    nomToDisplay: nomPrenomGetter(contact)
      + (contact.email ? ' - ' + contact.email : '')
      + (contact.service ? ' - ' + contact.service : '')
      + (contact.fonction ? ' - ' + contact.fonction : '')
      + (typeContact ? ' - ' + typeContact : ''),
    nom: contact.nom,
    prenom: contact.prenom,
    archive: contact.archive,
  };
}

export default async function generationMail(intervenants, destinataireId, user) {
  const userAffixe1 = store.getters['permissions/userAffixe1'];
  const mail = genererMail();
  const promiseDestinatairesEnCopie = [];
  intervenants.filter((intervenant) => {
    return intervenant.destinataireEnCopie;
  }).forEach((intervenant) => {
    promiseDestinatairesEnCopie.push(getTiers(intervenant.tiersId));
  });
  const detailsDestinatairesEnCopie = await Promise.all(promiseDestinatairesEnCopie);
  detailsDestinatairesEnCopie.forEach((detailDestinataireEnCopie) => {
    if (detailDestinataireEnCopie.data.result.email) {
      mail.cc.push(convertTierToMail(detailDestinataireEnCopie.data.result));
    }
  });
  if (destinataireId) {
    const destinataire = (await getTiers(destinataireId)).data.result;
    if (destinataire && destinataire.email) mail.to.push(convertTierToMail(destinataire));
  }
  if (user.email) {
    mail.from = user.email;
    if (!userAffixe1) mail.cci.push(convertUserToMail(user));
  }
  return mail;
}

function getContactForFacture(facture, contacts) {
  const users = store.getters['parametres/parametre'](PARAMETRES_KEYS.utilisateur).filter(u => u.actif);

  const formatedContacts = [];
  const contactOnFactureIds = facture.intervenants
    .map(i => i.intervenantContacts.map(ic => ic.contactId)).flat(2);

  // Gestion des contacts mis sur la facture
  if (contactOnFactureIds.length > 0) {
    const allContact = contacts.filter(c => contactOnFactureIds.includes(c.id) && c.email);
    if (allContact.length > 0) {
      formatedContacts.push({
        nomCommercial: i18n.t('detailFacture.popUpMail.contactTitle'),
        contacts: allContact.map(convertContactToMail),
      });
    }
  }

  // Gestion des contacts sur les tiers
  facture.intervenants.forEach(intervenant => {
    const contactForInter = contacts.filter(c => c.tiersId === intervenant.tiersId
      && !contactOnFactureIds.includes(c.id)
      && c.email,
    );
    if (contactForInter.length > 0) {
      formatedContacts.push({
        nomCommercial: intervenant.tiers.nomCommercial,
        contacts: contactForInter.map(convertContactToMail),
      });
    }
  });

  const usersWithMail = users.filter(u => u.email);
  if (usersWithMail.length > 0) {
    formatedContacts.push({
      nomCommercial: i18n.t('mail.collaborateur'),
      contacts: usersWithMail.map(convertUserToMail),
    });
  }

  return formatedContacts;
}

export async function generationMailEnMasseFromFacture(factures) {
  const tiersFactures = factures.map(f => f.intervenants.map(i => i.tiersId)).flat(2);
  const tiersIds = uniq(tiersFactures);
  const promisesTiers = [];
  const promisesContacts = [];
  const userAffixe1 = store.getters['permissions/userAffixe1'];
  const user = store.getters['user/user'];

  tiersIds.forEach(tiersId => {
    promisesTiers.push(getTiers(tiersId));
    promisesContacts.push(getAllByTiers(tiersId));
  });

  const tiers = (await Promise.all(promisesTiers)).map(tr => tr.data.result);
  const contacts = (await Promise.all(promisesContacts))
    .map((rslt) => rslt.data.result.items)
    .flat(2);

  // Faire un foreach sur les factures
  const factureForMails = factures.map(facture => {
    const mailFacture = cloneDeep(facture);
    const intervenants = mailFacture.intervenants;

    // Gestion des destinataires
    mailFacture.contacts = getContactForFacture(facture, contacts);
    mailFacture.filteredContacts = mailFacture.contacts;

    // Gestion du mail
    const mail = genererMail();
    intervenants.filter(i => i.destinataireEnCopie).forEach(i => {
      const iTiers = tiers.find(t => t.id === i.tiersId);
      if (iTiers.email) mail.to.push(convertTierToMail(iTiers));
    });

    // Gestion contact unique sur facture
    const contactOnFactureIds = intervenants
      .map(i => i.intervenantContacts.map(ic => ic.contactId))
      .flat(2);

    if (contactOnFactureIds.length === 1) {
      const contact = contacts.find(c => c.id === contactOnFactureIds[0]);
      if (contact && contact.email) mail.to.push(convertContactToMail(contact));
    }

    // Gestion destinataire
    const destinataire = intervenants.find(i => i.destinataire);
    if (destinataire && destinataire.email) mail.to.push(convertTierToMail(destinataire));

    if (user.email) {
      mail.from = user.email;
      if (!userAffixe1) mail.cci.push(convertUserToMail(user));
    }

    mail.objet = i18n.t('detailFacture.popUpMail.factureObject', {
      code: facture.code,
      date: formatDate(facture.dateDevisCommande),
    });

    mailFacture.mail = mail;

    return mailFacture;
  });

  return factureForMails;
}
